/**
 * @fileoverview Shared document theme editor: named preset swatches + custom color/font
 * overrides for the document builders (Phase E2). Used by both tenant-web and system-web so
 * the theme controls can no longer diverge (G-THEME-SW-01, G-DRY-01).
 * @module ui-admin/template-builder/documentSectionEditors/DocumentThemeEditor
 *
 * Responsibilities: - Render the ColorThemePicker preset swatches plus custom Primary/Accent/
 *                     Heading color pickers and a document base-font selector.
 *                   - Report preset changes (onPresetChange) and custom-override changes
 *                     (onCustomChange) to the host page.
 * Dependencies:     - ./ColorThemePicker, @firesquirrel/document-render (ThemePresetId,
 *                     DocumentThemeCustom).
 * Architecture:     - Presentation-only, fully controlled (value + onChange). The page owns
 *                     state and persists via reportTemplateTransform.
 * Notes:            - Custom values are layered over the preset by the shared renderer
 *                     (renderDocumentCss); empty custom = fall back to preset. Per-element
 *                     styles (E1) still beat the theme. Clearing all custom fields emits
 *                     undefined so the "customized" indicator turns off.
 *                   - Optional department-default controls (Phase E2b) are rendered only when
 *                     the host page passes onSaveAsDepartmentDefault; the page owns the
 *                     local-first persistence (system_tenant_ref). system-web omits them.
 */

import type { ThemePresetId, DocumentThemeCustom } from '@firesquirrel/document-render';
import { ColorThemePicker } from './ColorThemePicker';

/**
 * Document base-font options (CSS font-family stacks). Presentation config, not data —
 * an empty value means "renderer default" (Arial/Helvetica/sans-serif).
 */
const FONT_OPTIONS: Array<{ label: string; value: string }> = [
  { label: 'Default (Arial / sans-serif)', value: '' },
  { label: 'Helvetica', value: "'Helvetica Neue', Helvetica, Arial, sans-serif" },
  { label: 'Georgia (serif)', value: 'Georgia, "Times New Roman", serif' },
  { label: 'Times New Roman (serif)', value: '"Times New Roman", Times, serif' },
  { label: 'Verdana', value: 'Verdana, Geneva, sans-serif' },
  { label: 'Tahoma', value: 'Tahoma, Geneva, sans-serif' },
  { label: 'Courier (monospace)', value: '"Courier New", Courier, monospace' },
];

const COLOR_FIELDS: Array<{ key: keyof DocumentThemeCustom; label: string; hint: string }> = [
  { key: 'primary', label: 'Primary', hint: 'Borders, accents, brand' },
  { key: 'accent', label: 'Accent', hint: 'Secondary / table headers' },
  { key: 'heading', label: 'Heading', hint: 'Title + section headings' },
];

/**
 * @component DocumentThemeEditor
 * Purpose:     Edit the document-wide theme — a named preset plus optional custom colors/font.
 * Props:       preset (ThemePresetId|undefined) + onPresetChange; custom (DocumentThemeCustom|
 *              undefined) + onCustomChange; disabled?.
 * Behavior:    Controlled. Picking a color sets that custom field; clearing it (or the
 *              "Clear custom theme" button) removes it; when no custom field remains, emits
 *              undefined. Picking a font sets/clears themeCustom.baseFont.
 * Data Source: local (THEME_PRESETS + FONT_OPTIONS are static config from this layer).
 * Constraints: presentation-only; no API calls; no Dexie access.
 * Performance: trivial (a few controlled inputs).
 */
export function DocumentThemeEditor({
  preset,
  onPresetChange,
  custom,
  onCustomChange,
  disabled = false,
  departmentDefaultActive = false,
  onSaveAsDepartmentDefault,
  onClearDepartmentDefault,
  departmentDefaultPending = false,
}: {
  preset: ThemePresetId | undefined;
  onPresetChange: (id: ThemePresetId) => void;
  custom: DocumentThemeCustom | undefined;
  onCustomChange: (custom: DocumentThemeCustom | undefined) => void;
  disabled?: boolean;
  /** When true, a department default document theme is currently saved (Phase E2b). */
  departmentDefaultActive?: boolean;
  /** When provided, renders the department-default controls (tenant-web only). Saves the
   *  current preset + custom as the department default for new documents. */
  onSaveAsDepartmentDefault?: () => void;
  /** Clears the saved department default. Only shown when departmentDefaultActive. */
  onClearDepartmentDefault?: () => void;
  /** True while a department-default write is in flight. */
  departmentDefaultPending?: boolean;
}) {
  const c = custom ?? {};

  const patch = (next: Partial<DocumentThemeCustom>) => {
    const merged: DocumentThemeCustom = { ...c, ...next };
    (Object.keys(merged) as Array<keyof DocumentThemeCustom>).forEach((k) => {
      const v = merged[k];
      if (v === undefined || v === '') delete merged[k];
    });
    onCustomChange(Object.keys(merged).length ? merged : undefined);
  };

  const hasCustom = Object.keys(c).length > 0;
  // A meaningful theme to save as the department default = a real preset and/or any custom
  // override. Without this guard, "Set as department default" with an empty theme would
  // normalize to undefined and silently clear an existing department default.
  const hasThemeToSave = (!!preset && preset !== 'none') || hasCustom;

  return (
    <div className="space-y-3">
      <ColorThemePicker value={preset} onChange={onPresetChange} disabled={disabled} />

      <div className="border-t border-gray-200 pt-2">
        <div className="flex items-center justify-between mb-2">
          <span className="text-xs font-medium text-gray-600">
            Custom colors {hasCustom && <span className="text-blue-600" title="Custom overrides active">•</span>}
          </span>
          {hasCustom && (
            <button
              type="button"
              disabled={disabled}
              onClick={() => onCustomChange(undefined)}
              className="text-[10px] text-gray-400 hover:text-gray-600 underline"
              data-testid="theme-custom-clear"
            >
              Clear custom theme
            </button>
          )}
        </div>

        <div className="grid grid-cols-3 gap-2">
          {COLOR_FIELDS.map(({ key, label, hint }) => {
            const value = c[key];
            return (
              <div key={key} className="flex flex-col items-start">
                <label className="text-[10px] text-gray-500 mb-0.5" title={hint}>{label}</label>
                <div className="flex items-center gap-1">
                  <input
                    type="color"
                    disabled={disabled}
                    value={typeof value === 'string' && value ? value : '#000000'}
                    onChange={(e) => patch({ [key]: e.target.value } as Partial<DocumentThemeCustom>)}
                    className="w-7 h-7 rounded-sm border border-gray-300 p-0 cursor-pointer disabled:cursor-not-allowed"
                    aria-label={`${label} color`}
                    data-testid={`theme-custom-${key}`}
                  />
                  {value && (
                    <button
                      type="button"
                      disabled={disabled}
                      onClick={() => patch({ [key]: undefined } as Partial<DocumentThemeCustom>)}
                      className="text-[10px] text-gray-400 hover:text-red-600"
                      title={`Clear ${label}`}
                      data-testid={`theme-custom-${key}-clear`}
                    >
                      ×
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        <div className="mt-2">
          <label className="text-[10px] text-gray-500 block mb-0.5">Base font</label>
          <select
            disabled={disabled}
            value={c.baseFont ?? ''}
            onChange={(e) => patch({ baseFont: e.target.value || undefined })}
            className="w-full px-2 py-1 border border-gray-300 rounded-sm text-xs"
            data-testid="theme-custom-baseFont"
          >
            {FONT_OPTIONS.map((f) => (
              <option key={f.label} value={f.value}>{f.label}</option>
            ))}
          </select>
        </div>

        <p className="text-[10px] text-gray-400 mt-1">
          Custom colors override the preset for this document. Per-element styles still win.
        </p>
      </div>

      {onSaveAsDepartmentDefault && (
        <div className="border-t border-gray-200 pt-2">
          <div className="flex items-center justify-between">
            <span className="text-xs font-medium text-gray-600">
              Department default {departmentDefaultActive && (
                <span className="text-green-600" title="A department default document theme is set">•</span>
              )}
            </span>
            {departmentDefaultActive && onClearDepartmentDefault && (
              <button
                type="button"
                disabled={disabled || departmentDefaultPending}
                onClick={onClearDepartmentDefault}
                className="text-[10px] text-gray-400 hover:text-gray-600 underline disabled:opacity-50"
                data-testid="theme-dept-default-clear"
              >
                Clear department default
              </button>
            )}
          </div>
          <button
            type="button"
            disabled={disabled || departmentDefaultPending || !hasThemeToSave}
            onClick={onSaveAsDepartmentDefault}
            title={hasThemeToSave ? undefined : 'Pick a preset or custom color/font first'}
            className="mt-1 w-full px-2 py-1 border border-gray-300 rounded-sm text-xs text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            data-testid="theme-dept-default-save"
          >
            {departmentDefaultPending ? 'Saving…' : 'Set as department default'}
          </button>
          <p className="text-[10px] text-gray-400 mt-1">
            New documents start from this theme. Existing documents are not changed.
            {departmentDefaultActive && onClearDepartmentDefault && ' Use Clear to remove the default.'}
          </p>
        </div>
      )}
    </div>
  );
}
